import { Icon, Menu, Render } from "@mindlercare/ui-web";
import { useFlag } from "@mindlercare/unleash-client";
import { Typography } from "components/typography";
import { getTemplateLabel } from "core/functions/template";
import { useSelectedPsychologistIdParam } from "hooks/useSelectedPsychologistParam";
import { useSelectedTemplate } from "hooks/useSelectedTemplate";
import {
  useSelectedTemplates,
  useTemplates,
  type SelectedTemplate,
} from "hooks/useTemplates";
import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import useMarketStrategy from "strategies/market/useMarketStrategy";
import { useUserHasRole } from "../../../../hooks/userHooks";
import { useWeekTemplates } from "../../hooks/useWeekTemplates";
import { CalendarTemplates } from "./CalendarTemplates";
import {
  Container,
  ButtonContainer,
  SelectedTemplateButton,
  StyledMenuTrigger,
  MenuGroup,
  CategoryTitle,
  MenuItem,
  Divider,
} from "./selectTemplateButton.styles";
import CONSTANTS from "core/constants";

const getCategorizedSelectedTemplates = (
  selectedTemplates: SelectedTemplate[],
) => {
  const slotCategories = {
    bookable_slots: [
      CONSTANTS.SLOT_TEMPLATES.NEW_VISIT,
      CONSTANTS.SLOT_TEMPLATES.COUPLES_THERAPY,
      CONSTANTS.SLOT_TEMPLATES.REVISIT,
      CONSTANTS.SLOT_TEMPLATES.SHORT_VISIT,
      CONSTANTS.SLOT_TEMPLATES.TECH_TEST,
      CONSTANTS.SLOT_TEMPLATES.ADHD_SCREENING,
      CONSTANTS.SLOT_TEMPLATES.ADVISORY_CONSULT,
      CONSTANTS.SLOT_TEMPLATES.DIAGNOSTICS_JOURNAL_ENTRY,
      CONSTANTS.SLOT_TEMPLATES.EMDR,
      CONSTANTS.SLOT_TEMPLATES.LONG_VISIT,
      CONSTANTS.SLOT_TEMPLATES.EVALUATION,
    ],
    administrative_slots: [
      CONSTANTS.SLOT_TEMPLATES.BLOCKER,
      CONSTANTS.SLOT_TEMPLATES.FEEDBACK,
      CONSTANTS.SLOT_TEMPLATES.CLINICAL_SUPERVISION,
      CONSTANTS.SLOT_TEMPLATES.CONTRACTED_OTHER,
      CONSTANTS.SLOT_TEMPLATES.LIVE_MANAGEMENT_30,
      CONSTANTS.SLOT_TEMPLATES.LIVE_MANAGEMENT_60,
      CONSTANTS.SLOT_TEMPLATES.MINDLER_ACADEMY,
      CONSTANTS.SLOT_TEMPLATES.DAILY_HUB,
      CONSTANTS.SLOT_TEMPLATES.SUPERVISION,
    ],
  } as const;

  const slotCategoryMap = Object.fromEntries(
    Object.entries(slotCategories).flatMap(([category, slots]) =>
      slots.map((slot) => [slot, category]),
    ),
  ) as Record<string, keyof typeof slotCategories>;

  return selectedTemplates.reduce(
    (acc, tpl) => {
      const category = slotCategoryMap[tpl.name];
      if (category) acc[category].push(tpl);
      return acc;
    },
    {
      bookable_slots: [] as typeof selectedTemplates,
      administrative_slots: [] as typeof selectedTemplates,
    },
  );
};

function SelectTemplateButton() {
  const { t } = useTranslation();
  const canUseWeekTemplates = useWeekTemplates();

  const { data: templates } = useTemplates();

  const [currentTemplate, setSelectedTemplate] = useSelectedTemplate();

  const selectedTemplates = useSelectedTemplates();
  const strategy = useMarketStrategy();
  const fullPsySchedule = useFlag("PSYCHOLOGIST_FULL_SCHEDULE");

  const [selectedPsychologistId] = useSelectedPsychologistIdParam();
  const isAdmin = useUserHasRole("Admin");

  const currentCalendarTemplateLabel = getTemplateLabel(
    currentTemplate,
    templates,
    strategy?.showRevisitLabelSuffix,
    fullPsySchedule,
  );

  const handleTemplateClick = useCallback(
    (template: SelectedTemplate) => {
      setSelectedTemplate(template);
    },
    [setSelectedTemplate],
  );

  const categorizedTemplates =
    getCategorizedSelectedTemplates(selectedTemplates);

  return (
    <Container>
      <ButtonContainer>
        <Render if={!!currentTemplate}>
          <SelectedTemplateButton
            onClick={() => setSelectedTemplate(null)}
            id="currentTemplateButton"
            data-testid="currentTemplateButton"
            variant="tertiary"
          >
            <span>{currentCalendarTemplateLabel}</span>
            <Icon.Close width={20} />
          </SelectedTemplateButton>
        </Render>
        <Menu>
          <StyledMenuTrigger
            variant="primary"
            data-testid="selectTemplateButton"
            disabled={isAdmin && !selectedPsychologistId}
            withExpandIcon
          >
            <Icon.Add />
            <Typography insensitive variant="h5">
              {t("CALENDAR_TEMPLATE_BUTTON")}
            </Typography>
          </StyledMenuTrigger>
          <Menu.Items data-testid="dropDownTemplateArea">
            {Object.entries(categorizedTemplates).map(
              ([category, templates]) =>
                templates.length > 0 && (
                  <MenuGroup key={category}>
                    <CategoryTitle>
                      <Typography variant="body-small-bold">
                        {t(category.toUpperCase())}
                      </Typography>
                    </CategoryTitle>
                    {templates.map((template) => (
                      <Menu.Item
                        key={`${template.name}-${template.defaultLength}`}
                        onClick={() => handleTemplateClick(template)}
                        data-testid={`dropDownTemplateItem-${template?.value || "UNKNOWN"}`}
                      >
                        <MenuItem
                          selected={
                            currentTemplate
                              ? template.value + template.defaultLength ===
                                currentTemplate.name +
                                  currentTemplate.defaultLength
                              : false
                          }
                        >
                          {template?.label}
                        </MenuItem>
                      </Menu.Item>
                    ))}
                  </MenuGroup>
                ),
            )}
            <Render if={canUseWeekTemplates}>
              <>
                <Divider />
                <CalendarTemplates />
              </>
            </Render>
          </Menu.Items>
        </Menu>
      </ButtonContainer>
    </Container>
  );
}

export default memo(SelectTemplateButton);
